import React, { useMemo, useState } from 'react';
import { CollapseBlock, MainTitle, Spinner } from '../../../common/index.jsx';
import {
  RadioButtonsPicker,
  YearsPicker,
} from '../../../common/form/FormElements.jsx';
import { useGetCalendarEventYearQuery } from '../../../../redux/services/events.jsx';
import { useTranslation } from 'react-i18next';
import AcademicCalendarTable from './AcademicCalendarTable.jsx';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useSelector } from 'react-redux';
import { IconButton } from '../../../common/controls/Controls';
import { useToastMessages } from '../../../../hooks/useToastMessages.jsx';
import useIsUserAdmin from '../../../../hooks/useIsUserAdmin';
import checkPermission from '../../../../utils/helpers/checkPermission';
import { routes } from '../../../../utils/helpers/ApiClient.jsx';
import { useDeleteCalendarEventMutation } from '../../../../redux/services/events.jsx';

const AcademicCalendar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth?.user);
  const isAdmin = useIsUserAdmin();
  const canCreate = checkPermission(user, 'calendar.store', isAdmin);
  const backUrlState = {
    state: { backUrl: `${location.pathname}${location.search}` },
  };
  const [searchParams, setSearchParams] = useSearchParams({
    type: 'all',
  });
  const type = searchParams.get('type') || 'all';

  // Determine the correct academic year start (YYYY)
  // If it's September (8) or later, academic year starts this calendar year, otherwise previous year
  const getDefaultAcademicStartYear = () => {
    const now = new Date();
    const month = now.getMonth(); // 0 = Jan, 8 = Sep
    return month >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  };

  const getDisplayedYearFromUrl = () => {
    const yearParam = searchParams.get('year');
    const year = Number(yearParam);

    return yearParam && Number.isInteger(year)
      ? year
      : getDefaultAcademicStartYear();
  };

  const [displayedYear, setDisplayedYear] = useState(getDisplayedYearFromUrl());

  const {
    data: calendar,
    isFetching,
    refetch,
  } = useGetCalendarEventYearQuery(displayedYear, {
    refetchOnMountOrArgChange: true,
  });

  const {
    successMessage,
    errorMessage,
    deleteSuccess: showDeleteSuccess,
  } = useToastMessages();
  const [deleteCalendarEvent] = useDeleteCalendarEventMutation();

  const handleDelete = async (calendarItem) => {
    try {
      await deleteCalendarEvent(calendarItem.id).unwrap();
      showDeleteSuccess();
      refetch();
    } catch {
      errorMessage({ message: t('Udalosť sa nepodarilo vymazať.') });
    }
  };

  const filteredCalendarEvents = useMemo(() => {
    return (
      (calendar &&
        calendar.filter((singleEvent) => {
          if (type === 'bc') {
            return singleEvent.B === 1 && singleEvent.P !== 1;
          }
          if (type === 'ing') {
            return singleEvent.E === 1 && singleEvent.P !== 1;
          }
          if (type === 'phd') {
            return singleEvent.D === 1 && singleEvent.P !== 1;
          }
          if (type === 'events') {
            return singleEvent.P === 1;
          }
          return true;
        })) ||
      []
    );
  }, [calendar, type]);

  return (
    <CollapseBlock>
      <MainTitle title={t('Akademický kalendár')} />
      <Row align="bottom" gutter={16}>
        <Col
          md={{ span: 6 }}
          sm={{ span: 24 }}
          gap={16}
          style={{ marginBottom: 4 }}
        >
          <YearsPicker
            title={t('Akademický rok')}
            minYear={new Date().getFullYear() - 5}
            maxYear={new Date().getFullYear() + 2}
            value={displayedYear}
            onChange={(option) => {
              setDisplayedYear(option.value);
              setSearchParams({
                type,
                year: option.value,
              });
            }}
            useAcademicFormat
          />
        </Col>
        <Col
          md={{ span: 18 }}
          sm={{ span: 24 }}
          style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}
        >
          <RadioButtonsPicker
            items={[
              { id: 'all', name: 'all', label: t('Všetky udalosti') },
              { id: 'bc', name: 'bc', label: t('Bakalárske') },
              { id: 'ing', name: 'ing', label: t('Inžinierske') },
              { id: 'phd', name: 'phd', label: t('Doktorandské') },
              { id: 'events', name: 'events', label: t('Podujatia') },
            ]}
            value={type}
            onChange={(id) => {
              setSearchParams({
                type: id,
                year: displayedYear,
              });
            }}
          />
          {canCreate && (
            <div
              style={{
                marginLeft: 'auto',
                alignSelf: 'center',
                marginBottom: '0.5rem',
              }}
            >
              <IconButton
                type="button"
                onClick={() => navigate(routes.cmsCalendarCreate, backUrlState)}
                tooltip={t('Pridať novú udalosť')}
                icon={<PlusOutlined />}
              />
            </div>
          )}
        </Col>
      </Row>
      <div style={{ position: 'relative' }}>
        <div>
          <Row>
            <Col md={{ span: 24 }} sm={{ span: 24 }} style={{ margin: '0px' }}>
              <button
                type="button"
                tabIndex={0}
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  cursor: 'pointer',
                  color: '#15489e',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={async () => {
                  //e.preventDefault();
                  try {
                    // Define the full URL to copy
                    const url = `${window.location.protocol}//${window.location.hostname}/Akademicky_kalendar_FRI?type=${type === 'all' ? '' : type}`;

                    // Copy to clipboard
                    await navigator.clipboard.writeText(url);
                    // Show a success notification
                    successMessage({
                      message: t(
                        'Odkaz na akademický kalendár FRI bol skopírovaný do schránky.',
                      ),
                    });
                  } catch (err) {
                    errorMessage({ message: t('Kopírovanie zlyhalo.') });
                  }
                }}
              >
                <CopyOutlined
                  style={{ marginRight: 8, paddingBottom: 3 }}
                  tooltip={t('Skopírovať odkaz')}
                />
                <span>
                  {t(
                    'Skopírovať odkaz na akademický kalendár FRI vo formáte ICS do schránky',
                  )}
                </span>
              </button>
            </Col>
          </Row>
          {isFetching ? (
            <Spinner />
          ) : (
            <Row style={{ marginTop: 24 }}>
              <AcademicCalendarTable
                calendar={filteredCalendarEvents}
                onEditClick={(record) =>
                  navigate(
                    `${routes.cmsCalendarEdit}/${record.id}`,
                    backUrlState,
                  )
                }
                onDelete={handleDelete}
              />
            </Row>
          )}
        </div>
      </div>
    </CollapseBlock>
  );
};

export default AcademicCalendar;
